/**
 * Pure direction mapping behind `ui/DirIcon` (DESIGN_SYSTEM.md §18 RTL).
 * No React, so the unit tests can run it under node.
 *
 * Names are logical, glyphs are physical:
 * - `next` / `prev`: chevrons for steppers, pagination, "open" rows. `next` points the way
 *   the text reads (left in Hebrew, right in English).
 * - `forward` / `back`: arrows for navigation. `back` points against the reading direction
 *   (right in Hebrew, left in English).
 */

export type DirIconName = 'next' | 'prev' | 'forward' | 'back';
export type TextDirection = 'rtl' | 'ltr';
export type DirGlyph = 'chevron-left' | 'chevron-right' | 'arrow-left' | 'arrow-right';

/** Languages written right to left (the app ships he/en; the rest is future-proofing). */
const RTL_LANGS = new Set(['he', 'iw', 'ar', 'fa', 'ur', 'yi']);

/** Text direction of a language code ("he", "he-IL", "en-GB"). Unknown or empty: `fallback`. */
export function directionForLanguage(lang: string | null | undefined, fallback: TextDirection = 'rtl'): TextDirection {
  if (!lang) return fallback;
  return RTL_LANGS.has(lang.toLowerCase().split('-')[0]) ? 'rtl' : 'ltr';
}

/** The physical glyph for a logical icon name in a text direction. */
export function dirGlyph(name: DirIconName, dir: TextDirection): DirGlyph {
  // "Toward the end of the line": right in LTR, left in RTL.
  const towardEnd = dir === 'rtl' ? 'left' : 'right';
  const towardStart = dir === 'rtl' ? 'right' : 'left';
  switch (name) {
    case 'next':
      return `chevron-${towardEnd}`;
    case 'prev':
      return `chevron-${towardStart}`;
    case 'forward':
      return `arrow-${towardEnd}`;
    case 'back':
      return `arrow-${towardStart}`;
  }
}
